import { useCallback, useEffect, useState } from "react";

export type ReaderTheme = "light" | "sepia" | "dark";
export type ReaderFontSize = "sm" | "base" | "lg";

export interface ReaderSettings {
  theme: ReaderTheme;
  fontSize: ReaderFontSize;
}

const KEY = "ssm-reader-settings";
const DEFAULT: ReaderSettings = { theme: "light", fontSize: "base" };
const listeners = new Set<() => void>();

function readSettings(): ReaderSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<ReaderSettings>;
    return {
      theme: parsed.theme || DEFAULT.theme,
      fontSize: parsed.fontSize || DEFAULT.fontSize,
    };
  } catch {
    return DEFAULT;
  }
}

let cache: ReaderSettings = readSettings();

function writeSettings(next: ReaderSettings) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore storage errors */
  }
  listeners.forEach((listener) => listener());
}

export function useReaderSettings() {
  const [settings, setSettings] = useState<ReaderSettings>(cache);

  useEffect(() => {
    const listener = () => setSettings({ ...cache });
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const setTheme = useCallback((theme: ReaderTheme) => {
    writeSettings({ ...cache, theme });
  }, []);

  const setFontSize = useCallback((fontSize: ReaderFontSize) => {
    writeSettings({ ...cache, fontSize });
  }, []);

  return { settings, setTheme, setFontSize };
}